import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { getCurrencyByLocation, formatPrice } from '../utils/currency';
import { getFallbackImage } from '../utils/fallbackImages';
import DistanceFilter from '../components/DistanceFilter';
import CategoryGrid from '../components/CategoryGrid';
import PageSeo from '../components/PageSeo';
import { SEO } from '../utils/seoConfig';

import {
  Drill, Droplets, Wind, Wrench, Cog, Activity, LineChart, TestTube,
  Anchor, Truck, Settings, FileSearch, HardHat, Box
} from 'lucide-react';

// Map Lucide icon names to components
const LUCIDE_ICONS = {
  Drill, Droplets, Wind, Wrench, Cog, Activity, LineChart, TestTube,
  Anchor, Truck, Settings, FileSearch, HardHat, Box
};

const getIcon = (iconName) => {
  const IconComponent = LUCIDE_ICONS[iconName] || Box;
  return <IconComponent size={16} />;
};

export default function Shop() {
  const { t, i18n } = useTranslation();
  const { API } = useContext(AppContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productCounts, setProductCounts] = useState({});
  const [locations, setLocations] = useState([]);
  const [shops, setShops] = useState([]);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedLocation, setSelectedLocation] = useState(searchParams.get('location') || 'all');
  const [selectedShop, setSelectedShop] = useState(searchParams.get('shop') || 'all');
  const [selectedListingType, setSelectedListingType] = useState(searchParams.get('listing_type') || 'all');
  const [shopName, setShopName] = useState('');
  const [centerCity, setCenterCity] = useState('');
  const [radiusKm, setRadiusKm] = useState(null);

  useEffect(() => {
    fetchCategories();
    fetchLocations();
    fetchShops();
  }, []);

  useEffect(() => {
    // Update filters when URL changes
    const shopParam = searchParams.get('shop');
    const locationParam = searchParams.get('location');
    const categoryParam = searchParams.get('category');
    const listingTypeParam = searchParams.get('listing_type');
    
    if (shopParam && shopParam !== selectedShop) {
      setSelectedShop(shopParam);
    }
    if (locationParam && locationParam !== selectedLocation) {
      setSelectedLocation(locationParam);
    }
    if (categoryParam && categoryParam !== selectedCategory) {
      setSelectedCategory(categoryParam);
    }
    if (listingTypeParam && listingTypeParam !== selectedListingType) {
      setSelectedListingType(listingTypeParam);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory, selectedLocation, selectedShop, selectedListingType, search, centerCity, radiusKm]);

  // Fetch categories, locations and shops on mount
  useEffect(() => {
    fetchCategories();
    fetchLocations();
    fetchShops();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?active_only=true`);
      // Sort by position field, filter active ones
      const sorted = response.data
        .filter(cat => cat.active !== false)
        .sort((a, b) => (a.position || 99) - (b.position || 99));
      setCategories(sorted);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Calculate product counts per category
  useEffect(() => {
    if (products.length > 0) {
      const counts = {};
      products.forEach(p => {
        const catId = p.category_id;
        counts[catId] = (counts[catId] || 0) + 1;
      });
      setProductCounts(counts);
    }
  }, [products]);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops`);
      setShops(response.data);
      
      // If a shop is selected, find its name
      if (selectedShop !== 'all') {
        const shop = response.data.find(s => s.id === selectedShop);
        if (shop) {
          setShopName(shop.name);
        }
      }
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      let url = `${API}/products`;
      const params = [];
      if (selectedCategory !== 'all') params.push(`category_id=${selectedCategory}`);
      if (selectedLocation !== 'all' && !centerCity) params.push(`location=${selectedLocation}`);
      if (selectedShop !== 'all') params.push(`shop_id=${selectedShop}`);
      if (selectedListingType !== 'all') params.push(`listing_type=${selectedListingType}`);
      if (search) params.push(`search=${search}`);
      if (centerCity && radiusKm) {
        params.push(`center_city=${encodeURIComponent(centerCity)}`);
        params.push(`radius_km=${radiusKm}`);
      }
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  // Handler pour le filtre de distance
  const handleDistanceFilter = (city, radius) => {
    console.log('Distance filter applied:', { city, radius });
    setCenterCity(city || '');
    setRadiusKm(city && radius ? radius : null);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="shop-page">
      <PageSeo {...SEO.shop} />
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540]" data-testid="shop-title">
            {(() => {
              if (selectedListingType === 'all') {
                return selectedShop !== 'all' && shopName ? shopName : t('nav.shop');
              }
              const types = selectedListingType.split(',').map(t => t.trim());
              if (types.length > 1) {
                return i18n.language === 'fr' ? '🛒 Sélection de produits' : '🛒 Product selection';
              }
              const titleMap = {
                location: i18n.language === 'fr' ? '📅 Matériel en location' : '📅 Equipment for rent',
                neuf: i18n.language === 'fr' ? '✨ Matériel neuf' : '✨ New equipment',
                occasion: i18n.language === 'fr' ? '🔄 Matériel d\'occasion' : '🔄 Used equipment',
                service: i18n.language === 'fr' ? '🔧 Services' : '🔧 Services',
                travaux: i18n.language === 'fr' ? '🏗️ Travaux' : '🏗️ Works',
                etude: i18n.language === 'fr' ? '📊 Études' : '📊 Studies',
                talent: i18n.language === 'fr' ? '👤 Talents' : '👤 Talents',
                presentation: i18n.language === 'fr' ? '🎯 Présentations' : '🎯 Presentations'
              };
              return titleMap[types[0]] || t('nav.shop');
            })()}
          </h1>
          {selectedShop !== 'all' && shopName && selectedListingType === 'all' && (
            <p className="text-gray-600 mt-2">
              {i18n.language === 'fr' ? 'Produits de ce fournisseur' : 'Products from this supplier'}
            </p>
          )}
          {selectedListingType !== 'all' && (
            <p className="text-gray-600 mt-2">
              {products.length} {i18n.language === 'fr' ? 'annonce(s) disponible(s)' : 'listing(s) available'}
            </p>
          )}
        </div>

        {/* Category Bar */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={() => {
                setSelectedCategory('all');
                setSearchParams(prev => {
                  prev.delete('category');
                  return prev;
                });
              }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                selectedCategory === 'all' 
                  ? 'bg-[#0A2540] text-white shadow-md' 
                  : 'bg-white border border-gray-200 hover:border-[#635BFF] hover:text-[#635BFF]'
              }`}
            >
              🏷️ {i18n.language === 'fr' ? 'Tous' : 'All'}
            </button>
            {categories.filter(cat => cat.active !== false).map(cat => (
              <button
                key={cat.id}
                onClick={() => {
                  setSelectedCategory(cat.id);
                  setSearchParams(prev => {
                    prev.set('category', cat.id);
                    return prev;
                  });
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === cat.id 
                    ? 'text-white shadow-md' 
                    : 'bg-white border border-gray-200 hover:border-[#635BFF] hover:text-[#635BFF]'
                }`}
                style={{
                  backgroundColor: selectedCategory === cat.id ? (cat.color || '#635BFF') : undefined
                }}
              >
                <span style={{ color: selectedCategory === cat.id ? 'white' : (cat.color || '#635BFF') }}>
                  {cat.icon_lucide ? getIcon(cat.icon_lucide) : (cat.icon || '📦')}
                </span>
                <span className="hidden sm:inline">{i18n.language === 'fr' ? cat.name_fr : cat.name_en}</span>
                {productCounts[cat.id] > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    selectedCategory === cat.id 
                      ? 'bg-white/20 text-white' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {productCounts[cat.id]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          {/* Active listing type filter banner */}
          {selectedListingType !== 'all' && (
            <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-purple-600 font-medium">
                  {i18n.language === 'fr' ? 'Filtre actif : ' : 'Active filter: '}
                </span>
                {selectedListingType.split(',').map((type) => {
                  const typeLabels = {
                    location: '📅 Location',
                    neuf: '✨ Neuf',
                    occasion: '🔄 Occasion',
                    service: '🔧 Service',
                    travaux: '🏗️ Travaux',
                    etude: '📊 Étude',
                    talent: '👤 Talent',
                    presentation: '🎯 Présentation'
                  };
                  return (
                    <span key={type.trim()} className="bg-purple-500 text-white px-2 py-1 rounded text-sm">
                      {typeLabels[type.trim()] || type.trim()}
                    </span>
                  );
                })}
              </div>
              <button
                onClick={() => {
                  setSelectedListingType('all');
                  setSearchParams(prev => {
                    prev.delete('listing_type');
                    return prev;
                  });
                }}
                className="text-purple-600 hover:text-purple-800 text-sm font-medium flex items-center gap-1"
              >
                <FaTimes className="w-3 h-3" />
                {i18n.language === 'fr' ? 'Retirer le filtre' : 'Remove filter'}
              </button>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Input
                type="text"
                placeholder={t('search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder={t('filter')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category.id} value={category.id}>
                    {i18n.language === 'fr' ? category.name_fr : category.name_en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedListingType} onValueChange={(value) => {
              setSelectedListingType(value);
              if (value !== 'all') {
                setSearchParams(prev => {
                  prev.set('listing_type', value);
                  return prev;
                });
              } else {
                setSearchParams(prev => {
                  prev.delete('listing_type');
                  return prev;
                });
              }
            }}>
              <SelectTrigger data-testid="listing-type-filter">
                <SelectValue placeholder={i18n.language === 'fr' ? "Type d'offre" : 'Offer type'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{i18n.language === 'fr' ? 'Tous les types' : 'All types'}</SelectItem>
                <SelectItem value="neuf">✨ Neuf</SelectItem>
                <SelectItem value="occasion">🔄 Occasion</SelectItem>
                <SelectItem value="location">📅 Location</SelectItem>
                <SelectItem value="service">🔧 Service</SelectItem>
                <SelectItem value="travaux">🏗️ Travaux</SelectItem>
                <SelectItem value="etude">📊 Étude</SelectItem>
                <SelectItem value="talent">👤 Talent</SelectItem>
                <SelectItem value="presentation">🎯 Présentation</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger data-testid="location-filter">
                <SelectValue placeholder={i18n.language === 'fr' ? 'Localisation' : 'Location'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                {locations.map(location => (
                  <SelectItem key={location} value={location}>
                    {location}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedShop} onValueChange={setSelectedShop}>
              <SelectTrigger data-testid="shop-filter">
                <SelectValue placeholder={i18n.language === 'fr' ? 'Fournisseur' : 'Supplier'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{i18n.language === 'fr' ? 'Tous les fournisseurs' : 'All suppliers'}</SelectItem>
                {shops.map(shop => (
                  <SelectItem key={shop.id} value={shop.id}>
                    {shop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Distance Filter */}
          <div className="mt-4">
            <DistanceFilter onFilterChange={handleDistanceFilter} />
          </div>
        </div>

        {/* Products Grid - Annonces Parlantes */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map(product => {
            const productName = i18n.language === 'fr' ? product.name_fr : product.name_en;
            const fallbackImg = getFallbackImage(productName);
            const validImages = (product.images || []).filter(img => img && img.trim() !== '');
            const imgSrc = validImages[0] || fallbackImg;
            const currency = getCurrencyByLocation(product.location);
            
            // Déterminer le type de listing avec badge
            const getListingBadge = () => {
              const type = product.listing_type;
              if (type === 'neuf') return { label: 'NEUF', color: 'bg-blue-500', icon: '✨' };
              if (type === 'occasion') return { label: 'OCCASION', color: 'bg-amber-500', icon: '🔄' };
              if (type === 'reconditionne') return { label: 'RECONDITIONNÉ', color: 'bg-green-500', icon: '♻️' };
              if (type === 'location') return { label: 'LOCATION', color: 'bg-purple-500', icon: '📅' };
              if (type === 'service') return { label: 'SERVICE', color: 'bg-emerald-500', icon: '🔧' };
              if (type === 'travaux') return { label: 'TRAVAUX', color: 'bg-orange-500', icon: '🏗️' };
              if (type === 'etude') return { label: 'ÉTUDE', color: 'bg-cyan-500', icon: '📊' };
              if (type === 'talent') return { label: 'TALENT', color: 'bg-pink-500', icon: '👤' };
              if (type === 'presentation') return { label: 'PRÉSENTATION', color: 'bg-indigo-500', icon: '🎯' };
              if (product.rental_available) return { label: 'LOCATION', color: 'bg-purple-500', icon: '📅' };
              if (product.price_used && !product.price_new) return { label: 'OCCASION', color: 'bg-amber-500', icon: '🔄' };
              if (product.price_new) return { label: 'NEUF', color: 'bg-blue-500', icon: '✨' };
              return null;
            };
            
            const listingBadge = getListingBadge();
            
            // Extraire les specs clés (marque, modèle, année si disponibles)
            const specs = [];
            if (product.brand) specs.push({ label: 'Marque', value: product.brand });
            if (product.model) specs.push({ label: 'Modèle', value: product.model });
            if (product.year) specs.push({ label: 'Année', value: product.year });
            if (product.hours) specs.push({ label: 'Heures', value: `${product.hours}h` });
            
            // Prix principal à afficher
            const getMainPrice = () => {
              if (product.show_price === false) return { type: 'contact', label: 'Prix sur demande' };
              if (product.price_used) return { type: 'used', value: product.price_used, label: 'Occasion' };
              if (product.price_new) return { type: 'new', value: product.price_new, label: 'Neuf' };
              if (product.price_rental) return { type: 'rental', value: product.price_rental, label: '/jour' };
              return { type: 'quote', label: 'Sur devis' };
            };
            
            const mainPrice = getMainPrice();
            
            return (
              <Link key={product.id} to={`/product/${product.id}`} data-testid={`product-${product.id}`}>
                <Card className="product-card h-full group hover:shadow-xl transition-all duration-300 overflow-hidden border-0 shadow-md">
                  <CardContent className="p-0 relative">
                    {/* Badges superposés */}
                    <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
                      {listingBadge && (
                        <span className={`${listingBadge.color} text-white px-2 py-1 rounded text-xs font-bold shadow-lg`}>
                          {listingBadge.icon} {listingBadge.label}
                        </span>
                      )}
                      {product.managed_by_geots && (
                        <span className="bg-[#0A2540] text-white px-2 py-1 rounded text-xs font-bold shadow-lg">
                          🛡️ Géré GEOTS
                        </span>
                      )}
                    </div>
                    
                    {product.featured && (
                      <div className="absolute top-3 right-3 z-10 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-white px-2 py-1 rounded text-xs font-bold shadow-lg">
                        ⭐ Vedette
                      </div>
                    )}
                    
                    {/* Image avec overlay au hover */}
                    <div className="relative overflow-hidden">
                      <img 
                        src={imgSrc} 
                        alt={productName}
                        className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = fallbackImg;
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                    
                    {/* Contenu */}
                    <div className="p-4">
                      {/* Titre avec marque */}
                      <div className="mb-2">
                        {product.brand && (
                          <span className="text-xs text-[#635BFF] font-semibold uppercase tracking-wide">
                            {product.brand}
                          </span>
                        )}
                        <h3 className="font-bold text-lg text-[#0A2540] line-clamp-1 group-hover:text-[#635BFF] transition-colors">
                          {productName}
                        </h3>
                      </div>
                      
                      {/* Localisation */}
                      {product.location && (
                        <p className="text-xs text-gray-500 mb-2 flex items-center">
                          <svg className="w-3 h-3 mr-1 text-[#635BFF]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          {product.location}
                        </p>
                      )}
                      
                      {/* Specs clés (si disponibles) */}
                      {specs.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {specs.slice(0, 3).map((spec, idx) => (
                            <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                              {spec.value}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Description courte */}
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {i18n.language === 'fr' ? product.description_fr : product.description_en}
                      </p>
                      
                      {/* Prix principal - Design amélioré */}
                      <div className="pt-3 border-t border-gray-100">
                        {mainPrice.type === 'contact' || mainPrice.type === 'quote' ? (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">{mainPrice.label}</span>
                            <span className="bg-[#635BFF] text-white px-3 py-1 rounded-full text-sm font-semibold">
                              Nous contacter
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="text-2xl font-bold text-[#0A2540]">
                                {formatPrice(mainPrice.value, currency)}
                              </span>
                              <span className="text-xs text-gray-500 ml-1">
                                {mainPrice.type === 'rental' ? '/jour HT' : 'HT'}
                              </span>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              mainPrice.type === 'new' ? 'bg-blue-100 text-blue-700' :
                              mainPrice.type === 'used' ? 'bg-amber-100 text-amber-700' :
                              'bg-purple-100 text-purple-700'
                            }`}>
                              {mainPrice.label}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* CTA hover */}
                      <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-[#635BFF] text-white text-center py-2 rounded-lg text-sm font-semibold">
                          Voir les détails →
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {products.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">{t('loading')}</p>
          </div>
        )}
      </div>
    </div>
  );
}